<template>
  <div class="criteria-form">
    <div>
      <label for="insurance-overnight">Where is the vehicle to be primarily kept overnight?<span class="req">*</span></label>
      <multiselect
        id="insurance-overnight"
        v-model="overnightLocation"
        placeholder=""
        :multiple="false"
        :options="overnightLocationOptions"
        :show-labels="false"
      >
      </multiselect>
    </div>
    <div>
      <div class="declaration-row agree">
        <input type="checkbox" name="confirm" id="confirm" class="confirm" v-model="acceptanceOfSalsacConditionsAndInsurance">
        <label for="confirm">{{ product.scheme_card_insurance_declaration_text }}</label>
      </div>
    </div>
    <div class="submit">
      <button class="button button--steelBlue float-end" @click="save">SAVE</button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, getCurrentInstance } from 'vue';
import Multiselect from "vue-multiselect";
import {EventBus} from "@/event-bus";

const { proxy } = getCurrentInstance();

const props = defineProps({
  profile: {
    type: Object,
    required: true,
  },
  product: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['insurance-saved', 'percent-updated']);

// Percentage: 2 required fields (overnight location + declaration acceptance)
const isFilled = v => v !== null && v !== undefined && v !== '' && v !== false;

const insurancePercent = computed(() => {
  const fields = [
    props.profile.primaryOvernightVehicleLocation,
    props.profile.acceptanceOfSalarySacrificeConditionsOfUseAndInsurance,
  ];
  return Math.round((fields.filter(isFilled).length / fields.length) * 100);
});

watch(insurancePercent, val => emit('percent-updated', val), { immediate: true });

const overnightLocation = ref(props.profile.primaryOvernightVehicleLocation);
const acceptanceOfSalsacConditionsAndInsurance = ref(!!props.profile.acceptanceOfSalarySacrificeConditionsOfUseAndInsurance);

const overnightLocationOptions = computed(() => {
  const options = [props.profile.postcode];

  if (props.profile.additionalDrivers) {
    props.profile.additionalDrivers.forEach(driver => {
      if (driver.postcode && !options.includes(driver.postcode)) {
        options.push(driver.postcode);
      }
    });
  }

  return options.sort();
});

function save() {
  if (!overnightLocation.value) {
    alert('Please select an overnight location before saving.');
    return;
  }

  if (!acceptanceOfSalsacConditionsAndInsurance.value) {
    alert('Please accept the conditions and insurance declaration before saving.');
    return;
  }

  EventBus.$emit('loadingTitle', 'Updating insurance criteria & declaration acceptance');
  EventBus.$emit('loading', true);

  proxy.$http.post('/api/update-salsac-insurance-criteria', {
    'primaryOvernightVehicleLocation': overnightLocation.value,
    'acceptanceOfSalarySacrificeConditionsOfUseAndInsurance': !!acceptanceOfSalsacConditionsAndInsurance.value
  })
    .then(response => {
      EventBus.$emit('loading', false);

      proxy.$notify({
        group: 'notify',
        type: 'success',
        title: 'Success',
        text: 'Acceptance saved successfully'
      });

      emit('insurance-saved', {
        primaryOvernightVehicleLocation: overnightLocation.value,
        acceptanceOfSalarySacrificeConditionsOfUseAndInsurance: !!acceptanceOfSalsacConditionsAndInsurance.value,
        profileCompleted: response.data.profileCompleted,
        insuranceQuestionIssues: response.data.insuranceQuestionIssues,
      });
    })
    .catch(err => {
      EventBus.$emit('loading', false);
      let response = err.response.data;

      proxy.$notify({
        group: 'notify',
        type: 'error',
        title: 'Error',
        text: response.error.message
      });
    });
}

</script>

<style scoped>
.criteria-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.criteria-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-foreground, #1a1a1a);
  margin-bottom: 0.5rem;
  display: block;
}

.criteria-select {
  display: flex;
  height: auto;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  border-radius: 5px;
  background: rgba(61, 118, 139, 0.1);
  color: #3d768b;
  font-size: 0.875rem;
  padding: 0.8rem 0.75rem;
  cursor: pointer;
}

.criteria-select:focus {
  outline: none;
  box-shadow: 0 0 0 2px var(--color-ring, #3b82f6);
}

.criteria-select:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.criteria-select-value {
  pointer-events: none;
}

.criteria-chevron {
  width: 1rem;
  height: 1rem;
  opacity: 0.5;
}

.declaration-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background: rgba(61, 118, 139, 0.1);
  color: #3d768b;
  border: 1px solid rgba(61, 118, 139, 0.3);
  border-radius: 5px;
}

.declaration-row.agree {
  margin-top:10px;
  margin-bottom:10px;
}

.toggle-switch {
  display: inline-flex;
  height: 1.5rem;
  width: 2.75rem;
  flex-shrink: 0;
  cursor: pointer;
  align-items: center;
  border-radius: 9999px;
  border: 2px solid transparent;
  transition: background-color 0.2s;
  background-color: var(--color-input, #d1d5db);
}

.toggle-switch[data-state="checked"] {
  background-color: var(--color-primary, #3b82f6);
}

.toggle-switch:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--color-ring, #3b82f6), 0 0 0 4px var(--color-background, #fff);
}

.toggle-switch:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.toggle-thumb {
  pointer-events: none;
  display: block;
  height: 1.25rem;
  width: 1.25rem;
  border-radius: 9999px;
  background: var(--color-background, #fff);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s;
  transform: translateX(0);
}

.toggle-thumb[data-state="checked"] {
  transform: translateX(1.25rem);
}
</style>
